import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';

const TIMEZONES = [
  'America/Anchorage',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/New_York',
  'America/Toronto',
  'America/Vancouver',
  'America/Winnipeg',
  'Atlantic/Halifax',
  'Canada/Atlantic',
  'Canada/Central',
  'Canada/Eastern',
  'Canada/Mountain',
  'Canada/Pacific',
];

interface LocaleSettingsStepProps {
  onNext: (data: any) => void;
  onBack: () => void;
  existingData?: any;
  isFirstStep?: boolean;
  isLastStep?: boolean;
}

export const LocaleSettingsStep: React.FC<LocaleSettingsStepProps> = ({
  onNext,
  onBack,
  existingData = {},
  isFirstStep = false,
  isLastStep = false,
}) => {
  const [weightUnit, setWeightUnit] = useState(existingData?.weightUnit || 'kg');
  const [distanceUnit, setDistanceUnit] = useState(existingData?.distanceUnit || 'km');
  const [timezone, setTimezone] = useState(existingData?.timezone || 'America/Toronto');
  const [error, setError] = useState('');

  const handleSubmit = () => {
    setError('');

    // All fields have defaults, so no validation needed
    const data = {
      weightUnit,
      distanceUnit,
      timezone,
    };

    onNext(data);
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
        Help us show information in your preferred units and timezone.
      </Typography>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Weight Unit */}
        <Box>
          <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Weight unit</Typography>
          <FormControl fullWidth>
            <Select
              value={weightUnit}
              onChange={(e) => setWeightUnit(e.target.value)}
            >
              <MenuItem value="kg">Kilograms (KG)</MenuItem>
              <MenuItem value="lbs">Pounds (LBS)</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Distance Unit */}
        <Box>
          <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Distance unit</Typography>
          <FormControl fullWidth>
            <Select
              value={distanceUnit}
              onChange={(e) => setDistanceUnit(e.target.value)}
            >
              <MenuItem value="km">Kilometers (KM)</MenuItem>
              <MenuItem value="mi">Miles (MI)</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Timezone */}
        <Box>
          <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Time Zone</Typography>
          <FormControl fullWidth>
            <Select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
            >
              {TIMEZONES.map((tz) => (
                <MenuItem key={tz} value={tz}>
                  {tz}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', gap: 2, mt: 4, justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          onClick={onBack}
          sx={{ minWidth: 120 }}
        >
          Previous Step
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          sx={{ minWidth: 120 }}
        >
          Continue
        </Button>
      </Box>
    </Box>
  );
};
